import { Controller } from "@hotwired/stimulus"

// Manages the push notification permission toggle and test button.
// Uses window.ChatPush (set in chat/field layout) for permission requests.
export default class extends Controller {
  static targets = ["toggle", "track", "knob", "statusText", "testBtn"]
  static values  = { testUrl: String }

  connect() {
    this.updateUI()

    // Log push events from the service worker into the diagnostic log
    this._swMessageHandler = (event) => {
      if (event.data?.type === "PUSH_RECEIVED") {
        this._logPush("event", `Push received: ${event.data.title}`, { body: event.data.body })
      }
    }
    navigator.serviceWorker?.addEventListener("message", this._swMessageHandler)
  }

  disconnect() {
    navigator.serviceWorker?.removeEventListener("message", this._swMessageHandler)
  }

  updateUI() {
    const permission = this.permission
    const isGranted  = permission === "granted"
    const isBlocked  = permission === "denied"

    if (this.hasTrackTarget) {
      this.trackTarget.classList.toggle("bg-sapphire", isGranted)
      this.trackTarget.classList.toggle("bg-slate-200", !isGranted)
    }
    if (this.hasKnobTarget) {
      this.knobTarget.classList.toggle("translate-x-4", isGranted)
    }
    if (this.hasToggleTarget) {
      this.toggleTarget.disabled = isBlocked
    }

    if (this.hasStatusTextTarget) {
      if (isGranted) {
        this.statusTextTarget.textContent = "On"
      } else if (isBlocked) {
        this.statusTextTarget.textContent = "Blocked in browser settings"
      } else if (!("Notification" in window)) {
        this.statusTextTarget.textContent = "Not supported on this device"
      } else {
        this.statusTextTarget.textContent = "Off"
      }
    }

    // Show test button only when notifications are enabled
    if (this.hasTestBtnTarget) {
      this.testBtnTarget.classList.toggle("hidden", !isGranted)
    }
  }

  toggle() {
    if (this.permission === "granted") {
      this.unsubscribe()
    } else if (this.permission !== "denied") {
      if (window.ChatPush) {
        window.ChatPush.requestPermission()
        this.pollForPermissionChange()
      }
    }
  }

  async sendTest() {
    if (!this.hasTestBtnTarget || !this.testUrlValue) return

    this.testBtnTarget.disabled = true
    this.testBtnTarget.textContent = "Sending..."

    try {
      const res = await fetch(this.testUrlValue, {
        method:  "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          "Accept": "application/json"
        }
      })

      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        this.testBtnTarget.textContent = "Sent!"
        this._logPush("info", `Test notification sent (${data.sent} subscription${data.sent === 1 ? '' : 's'})`)
      } else {
        this.testBtnTarget.textContent = "Failed"
        this._logPush("error", "Test notification failed", data)
        console.warn("[push-notifications] Test failed:", data.error, data.errors)
      }
    } catch (err) {
      this.testBtnTarget.textContent = "Failed"
      this._logPush("error", "Test notification request failed", { message: err.message })
    }

    setTimeout(() => {
      this.testBtnTarget.textContent = "Test"
      this.testBtnTarget.disabled = false
    }, 2000)
  }

  unsubscribe() {
    if (!("serviceWorker" in navigator)) return
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        if (!sub) return
        const endpoint = sub.endpoint
        sub.unsubscribe().then(() => {
          fetch(`/push_subscriptions/${encodeURIComponent(endpoint)}`, {
            method:  "DELETE",
            headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content }
          }).catch(() => {})
          this.updateUI()
        })
      })
    })
  }

  pollForPermissionChange() {
    let checks = 0
    const interval = setInterval(() => {
      checks++
      if (this.permission === "granted" || checks > 20) {
        clearInterval(interval)
        this.updateUI()
      }
    }, 500)
  }

  get permission() {
    return "Notification" in window ? Notification.permission : "unavailable"
  }

  async _logPush(level, message, detail = null) {
    try {
      // Dynamically import the correct logger based on the app
      const isField = window.location.pathname.startsWith("/field")
      const logger = isField
        ? await import("../field_logger")
        : await import("../chat_logger")

      const fn = level === "error" ? logger.logError
               : level === "warn"  ? logger.logWarn
               : level === "event" ? logger.logEvent
               : logger.logInfo
      fn("push", message, detail)
    } catch { /* logger unavailable */ }
  }
}
